/**
 * Aviator — every sound, synthesised. Platform pattern (gems_mine/sound.ts):
 * one lazy AudioContext, tone()/noise() primitives, a run-scoped gain
 * register so duck() and the visibility suspend cover everything, a
 * lookahead scheduler for the bed. New here: hold() — a steerable voice for
 * the engine hum and the bed drone (the only sounds that outlive a one-shot).
 *
 * Key: A minor pentatonic (A C D E G) — every one-shot sits inside the bed.
 */

const LS_SOUND = 'av_sound';
const LS_MUSIC = 'av_music';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let soundOn = true;
let musicOn = true;
let _duckedUntil = 0;

const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

export function initAvSound() { soundOn = lsGet(LS_SOUND) !== '0'; return soundOn; }
export function isAvSoundOn() { return soundOn; }
export function setAvSoundOn(v: boolean) { soundOn = v; lsSet(LS_SOUND, v ? '1' : '0'); if (!v) stopAvMusic(); else if (musicOn) startAvMusic(); return v; }
export function initAvMusic() { musicOn = lsGet(LS_MUSIC) !== '0'; return musicOn; }
export function isAvMusicOn() { return musicOn; }
export function setAvMusicOn(v: boolean) { musicOn = v; lsSet(LS_MUSIC, v ? '1' : '0'); if (v && soundOn) startAvMusic(); else stopAvMusic(); return v; }

function ac(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new AC();
    master = ctx.createGain(); master.gain.value = 1; master.connect(ctx.destination);
  } catch { ctx = null; }
  return ctx;
}
export function unlockAvSound() {
  const c = ac(); if (!c) return;
  if (c.state === 'suspended' && !document.hidden) void c.resume();
}

function bus(gain: number): { c: AudioContext; g: GainNode } | null {
  if (!soundOn) return null;
  const c = ac(); if (!c || !master) return null;
  const g = c.createGain(); g.gain.value = gain; g.connect(master);
  return { c, g };
}

function tone(freq: number, dur: number, o: { type?: OscillatorType; to?: number; gain?: number; delay?: number } = {}) {
  const b = bus(o.gain ?? 0.08); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const osc = b.c.createOscillator();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
  b.g.gain.setValueAtTime(0.0001, t0);
  b.g.gain.exponentialRampToValueAtTime(o.gain ?? 0.08, t0 + 0.006);
  b.g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(b.g); osc.start(t0); osc.stop(t0 + dur + 0.05);
}

function noise(dur: number, o: { f0?: number; f1?: number; q?: number; gain?: number; delay?: number } = {}) {
  const b = bus(o.gain ?? 0.06); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const n = Math.floor(b.c.sampleRate * dur);
  const buf = b.c.createBuffer(1, n, b.c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = b.c.createBufferSource(); src.buffer = buf;
  const f = b.c.createBiquadFilter(); f.type = 'bandpass';
  f.frequency.setValueAtTime(o.f0 ?? 1200, t0);
  if (o.f1) f.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur);
  f.Q.value = o.q ?? 1;
  src.connect(f); f.connect(b.g); src.start(t0);
}

/* ─── hold(): a steerable voice (hum, drone) ───────────────────────────── */

type Voice = { set(f: number, cut: number, a: number): void; cut(ms: number): void; stop(): void };
const voices = new Set<Voice>();

function hold(freq: number, o: { type?: OscillatorType; detune?: number; cutoff?: number; q?: number; gain?: number } = {}): Voice | null {
  const b = bus(o.gain ?? 0); if (!b) return null;
  const osc = b.c.createOscillator(); osc.type = o.type ?? 'sawtooth';
  osc.frequency.value = freq; osc.detune.value = o.detune ?? 0;
  const lp = b.c.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = o.cutoff ?? 400; lp.Q.value = o.q ?? 0.8;
  osc.connect(lp); lp.connect(b.g); osc.start();
  const tc = 0.045;
  const v: Voice = {
    set(f, cut, a) {
      const t = b.c.currentTime;
      osc.frequency.setTargetAtTime(f, t, tc);
      lp.frequency.setTargetAtTime(cut, t, tc);
      b.g.gain.setTargetAtTime(a, t, tc);
    },
    cut(ms) {
      const t = b.c.currentTime;
      b.g.gain.cancelScheduledValues(t); b.g.gain.setValueAtTime(b.g.gain.value, t);
      b.g.gain.linearRampToValueAtTime(0, t + ms / 1000);
    },
    stop() { try { osc.stop(b.c.currentTime + 0.05); } catch { /* already */ } voices.delete(v); },
  };
  voices.add(v);
  return v;
}

/* ─── the flight ──────────────────────────────────────────────────────── */

let humA: Voice | null = null, humB: Voice | null = null;
let hbOn = false, hbNext = 0;
let curM = 1;
const uOf = (m: number) => Math.min(1, Math.max(0, Math.log(m) / Math.log(500)));

/** takeoff rev + the hum fades in over it */
export function sTakeoff() {
  tone(70, 0.9, { type: 'sawtooth', to: 420, gain: 0.20 });
  tone(140, 0.9, { type: 'square', to: 840, gain: 0.07 });
  noise(0.9, { f0: 300, f1: 3200, q: 1.2, gain: 0.09 });
  tone(2200, 0.03, { type: 'square', to: 1800, gain: 0.09 });
  humA?.stop(); humB?.stop();
  humA = hold(55, { type: 'sawtooth', cutoff: 240, gain: 0 });
  humB = hold(55, { type: 'square', detune: 9, cutoff: 240, gain: 0 });
  window.setTimeout(() => { humA?.set(55, 240, 0.09); humB?.set(55, 240, 0.054); }, 300);
  hbOn = false;
  bedPhase('flight');
}

/** steer the hum + bed from the multiplier (≤30 Hz) */
export function sFlightTick(m: number) {
  curM = m;
  const u = uOf(m);
  const f = 55 * Math.pow(2, 2.4 * u), c = 240 * Math.pow(2, 3.3 * u), a = 0.09 + 0.07 * u;
  humA?.set(f, c, a); humB?.set(f, c, a * 0.6);
  bedIntensity(0.45 + 0.55 * u);
  if (!hbOn && m >= 5 && ctx) { hbOn = true; hbNext = ctx.currentTime; }
}

/** engine cut, doppler whine, thud */
export function sCrash() {
  hbOn = false;
  humA?.cut(40); humB?.cut(40);
  tone(1100, 0.7, { type: 'triangle', to: 190, gain: 0.15 });
  tone(1650, 0.7, { type: 'sine', to: 285, gain: 0.05 });
  tone(95, 0.38, { type: 'sine', to: 34, gain: 0.45, delay: 0.06 });
  noise(0.22, { f0: 140, f1: 55, q: 1, gain: 0.25, delay: 0.06 });
  duckAvMusic(1200);
  bedPhase('crash');
  const a = humA, b = humB; humA = humB = null;
  window.setTimeout(() => { a?.stop(); b?.stop(); }, 140);
}
export function sHeartbeatStop() { hbOn = false; }

/** cashout chime in three tiers by X */
export function sCashout(x: number) {
  if (x < 2) {
    tone(880, 0.12, { type: 'sine', gain: 0.16 });
    tone(1319, 0.22, { type: 'sine', gain: 0.14, delay: 0.09 });
    duckAvMusic(250);
  } else if (x < 10) {
    tone(880, 0.1, { type: 'triangle', gain: 0.15 });
    tone(1047, 0.1, { type: 'triangle', gain: 0.15, delay: 0.08 });
    tone(1319, 0.1, { type: 'triangle', gain: 0.16, delay: 0.16 });
    tone(1760, 0.4, { type: 'triangle', gain: 0.18, delay: 0.24 });
    noise(0.3, { f0: 6000, f1: 9000, q: 3, gain: 0.045, delay: 0.24 });
    duckAvMusic(400);
  } else {
    tone(880, 0.1, { type: 'triangle', gain: 0.15 });
    tone(1047, 0.1, { type: 'triangle', gain: 0.15, delay: 0.08 });
    tone(1319, 0.1, { type: 'triangle', gain: 0.16, delay: 0.16 });
    tone(1760, 0.9, { type: 'triangle', gain: 0.13, delay: 0.24 });
    tone(2637, 0.9, { type: 'sine', gain: 0.09, delay: 0.30 });
    tone(3520, 0.9, { type: 'sine', gain: 0.06, delay: 0.36 });
    noise(0.8, { f0: 7000, f1: 11000, q: 4, gain: 0.06, delay: 0.30 });
    tone(110, 0.35, { type: 'sine', to: 55, gain: 0.2, delay: 0.24 });
    duckAvMusic(700);
  }
}
export const sBet = (queued = false) => { const k = queued ? 0.6 : 1; tone(880, 0.06, { type: 'sine', gain: 0.13 * k }); tone(1319, 0.07, { type: 'sine', gain: 0.11 * k, delay: 0.05 }); noise(0.03, { f0: 3000, f1: 2200, q: 2, gain: 0.09 * k }); };
export const sCancel = () => { tone(1319, 0.06, { type: 'sine', gain: 0.11 }); tone(880, 0.09, { type: 'sine', to: 660, gain: 0.11, delay: 0.06 }); };
export const sDenied = () => { tone(175, 0.1, { type: 'square', gain: 0.09 }); tone(175, 0.1, { type: 'square', gain: 0.09, delay: 0.14 }); };
export const sTooLate = () => { tone(110, 0.28, { type: 'square', to: 98, gain: 0.12 }); tone(113, 0.28, { type: 'square', to: 101, gain: 0.08 }); noise(0.28, { f0: 900, f1: 600, q: 0.7, gain: 0.05 }); };
export const sLock = () => { tone(660, 0.05, { type: 'square', to: 520, gain: 0.07 }); };
export const sTap = () => { tone(880, 0.045, { type: 'sine', to: 620, gain: 0.05 }); };
export const sPop = () => { tone(1568, 0.08, { type: 'sine', to: 2093, gain: 0.12 }); noise(0.06, { f0: 5000, f1: 8000, q: 3, gain: 0.045, delay: 0.02 }); };
let lastTick = 0;
export const sCrowdTick = () => {
  const now = performance.now();
  if (now < _duckedUntil || now - lastTick < 250) return;
  lastTick = now;
  noise(0.025, { f0: 4500, f1: 3500, q: 3, gain: 0.03 });
};

/* ─── the bed: "Runway" — 96 BPM, A minor pentatonic, drone never stops ── */

const BAR = 2.5, LOOP = 8 * BAR;
let droneA: Voice | null = null, droneB: Voice | null = null, droneC: Voice | null = null;
let bedTimer = 0;
let loopStart = 0;
let nextEv = 0;
let I = 0.35;
let musicLive = false;

const BASS: number[][] = [
  [110, 110, 110, 110, 110, 110, 82.4, 82.4], [110, 110, 110, 110, 110, 110, 82.4, 82.4],
  [110, 110, 110, 110, 110, 110, 82.4, 82.4], [110, 110, 110, 110, 110, 110, 82.4, 82.4],
  [110, 110, 110, 110, 110, 110, 82.4, 82.4], [110, 110, 110, 110, 110, 110, 82.4, 82.4],
  [110, 110, 98, 98, 110, 110, 130.8, 130.8], [146.8, 146.8, 164.8, 164.8, 164.8, 164.8, 164.8, 164.8],
];
const LEAD: (number | null)[][] = [
  [440, null, 523.3, null], [659.3, null, 587.3, null], [523.3, null, 440, null], [392, null, 440, null],
  [440, null, 523.3, null], [659.3, null, 784, null], [659.3, null, 587.3, null], [523.3, null, 659.3, null],
];

function bedIntensity(v: number) {
  I = Math.min(1, Math.max(0, v));
  if (!musicLive) return;
  const g = 0.05 + 0.14 * I;
  const cut = 300 * Math.pow(2, 3 * I);
  droneA?.set(110, cut, 0.35 * g); droneB?.set(110.6, cut, 0.35 * g); droneC?.set(55, 200, 0.5 * g);
}
function bedPhase(p: 'betting' | 'flight' | 'crash' | 'intermission') {
  if (p === 'betting') bedIntensity(0.35);
  else if (p === 'flight') bedIntensity(0.45);
  else if (p === 'crash') { bedIntensity(0.12); window.setTimeout(() => bedIntensity(0.40), 1200); }
  else bedIntensity(0.40);
}
export const avBedPhase = bedPhase;

function pump() {
  const c = ctx; if (!c || !musicLive) return;
  const horizon = c.currentTime + 0.15;
  while (nextEv < horizon) {
    const pos = (nextEv - loopStart) % LOOP;
    const bar = Math.floor(pos / BAR) % 8;
    const beat16 = Math.round(((pos % BAR) / BAR) * 16) % 16;
    const d = Math.max(0, nextEv - c.currentTime);
    if (I >= 0.45 && beat16 % 2 === 0) {
      const f = BASS[bar][Math.floor(beat16 / 2)];
      tone(f, 0.16, { type: 'square', gain: 0.055 * (0.6 + 0.4 * I), delay: d });
    }
    if (I >= 0.55 && beat16 % 4 === 0) {
      const f = LEAD[bar][Math.floor(beat16 / 4)];
      if (f) tone(f, 1.1, { type: 'triangle', gain: 0.05 * (0.6 + 0.4 * I), delay: d });
    }
    if (I >= 0.70 && beat16 % 2 === 1) noise(0.02, { f0: 7000, f1: 5500, q: 2.5, gain: 0.025, delay: d });
    /* heartbeat rides the same scheduler */
    if (hbOn && hbNext < horizon) {
      const k = Math.log2(Math.max(5, curM) / 5);
      const bpm = Math.min(160, Math.max(60, 60 + 30 * k));
      const g = 0.15 + 0.13 * Math.min(1, Math.max(0, k / 4));
      const dd = Math.max(0, hbNext - c.currentTime);
      tone(58, 0.14, { type: 'sine', to: 38, gain: g, delay: dd });
      tone(52, 0.11, { type: 'sine', to: 34, gain: 0.7 * g, delay: dd + 0.16 });
      hbNext += 60 / bpm;
    }
    nextEv += BAR / 16;
  }
}

export function startAvMusic() {
  if (musicLive || !soundOn || !musicOn) return;
  const c = ac(); if (!c) return;
  musicLive = true;
  droneA = hold(110, { type: 'sawtooth', cutoff: 300, gain: 0 });
  droneB = hold(110.6, { type: 'sawtooth', cutoff: 300, gain: 0 });
  droneC = hold(55, { type: 'sine', cutoff: 200, gain: 0 });
  loopStart = c.currentTime + 0.1; nextEv = loopStart;
  bedIntensity(I);
  bedTimer = window.setInterval(pump, 25);
}
export function stopAvMusic() {
  musicLive = false;
  window.clearInterval(bedTimer);
  droneA?.stop(); droneB?.stop(); droneC?.stop(); droneA = droneB = droneC = null;
  _duckedUntil = 0;
}
/** drop the bed + hum, release over ms */
export function duckAvMusic(ms: number) {
  if (!master || !ctx) return;
  _duckedUntil = performance.now() + ms;
  const t = ctx.currentTime;
  master.gain.cancelScheduledValues(t);
  master.gain.setValueAtTime(master.gain.value, t);
  master.gain.linearRampToValueAtTime(0.35, t + 0.02);
  master.gain.linearRampToValueAtTime(1, t + 0.02 + ms / 1000);
}
export function bindAvVisibility() {
  const h = () => {
    const c = ctx; if (!c) return;
    if (document.hidden) void c.suspend();
    else if (soundOn) void c.resume();
  };
  document.addEventListener('visibilitychange', h);
  return () => document.removeEventListener('visibilitychange', h);
}
